import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Cpu, Gauge, Weight, Zap, Eye, Brain, Shield } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { PreOrderForm } from "@/components/PreOrderForm";
import puduD9Img from "@/assets/robots/pudu-d9.png";

const PuduD9 = () => {
  const [preOrderOpen, setPreOrderOpen] = useState(false);

  const specifications = [
    {
      icon: <Weight className="w-5 h-5" />,
      label: "Dimensions",
      value: "170cm Height × 65kg Weight"
    },
    {
      icon: <Cpu className="w-5 h-5" />,
      label: "Degrees of Freedom",
      value: "42 Independent DOF"
    },
    {
      icon: <Gauge className="w-5 h-5" />,
      label: "Joint Torque",
      value: "352 N·m Maximum"
    },
    {
      icon: <Zap className="w-5 h-5" />,
      label: "Payload Capacity",
      value: "20kg Maximum"
    },
    {
      icon: <Brain className="w-5 h-5" />,
      label: "Computing Power",
      value: "275 TOPS"
    },
    {
      icon: <Gauge className="w-5 h-5" />,
      label: "Walking Speed",
      value: "2 m/s Maximum"
    }
  ];

  const keyFeatures = [
    {
      icon: <Brain className="w-6 h-6" />,
      title: "Adult Body Proportions",
      description: "Designed with 170cm height and 65kg weight to match human adult proportions, enabling natural interaction and integration into human environments."
    },
    {
      icon: <Cpu className="w-6 h-6" />,
      title: "42 Degrees of Freedom",
      description: "Advanced articulation with 42 independent degrees of freedom and 352 N·m maximum joint torque for fluid, human-like movements."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "PUDU DH11 Dexterous Hands",
      description: "20kg maximum payload capacity with precision dexterous hands capable of complex manipulation tasks and delicate object handling."
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: "High-Precision Perception",
      description: "275 TOPS computing power enables advanced computer vision, spatial awareness, and real-time decision making for autonomous operation."
    },
    {
      icon: <Gauge className="w-6 h-6" />,
      title: "Advanced Mobility",
      description: "Bipedal locomotion with 2 m/s maximum walking speed, dynamic balance control, and ability to navigate complex environments."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Safe Human Interaction",
      description: "Built-in safety systems including collision detection, force control, and compliant behavior for safe operation alongside humans."
    }
  ];

  const applications = [
    {
      title: "Healthcare",
      description: "Patient assistance, medication delivery, vital signs monitoring, and support for medical staff in hospitals and care facilities."
    },
    {
      title: "Hospitality",
      description: "Guest services, concierge assistance, luggage handling, and interactive customer engagement in hotels and venues."
    },
    {
      title: "Research & Education",
      description: "Academic research platform for AI, robotics, and human-robot interaction studies in universities and research institutions."
    },
    {
      title: "Manufacturing",
      description: "Assembly assistance, quality inspection, material handling, and collaborative tasks in smart factory environments."
    },
    {
      title: "Retail",
      description: "Customer service, product demonstrations, inventory management, and interactive shopping experiences in stores."
    },
    {
      title: "Security & Inspection",
      description: "Facility patrol, security monitoring, routine inspections, and reporting in commercial and industrial settings."
    }
  ];

  return (
    <>
      <SEO 
        title="PUDU D9 - Full-Sized Bipedal Humanoid Robot | Pre-Order Now"
        description="The first full-sized bipedal humanoid robot by Pudu Robotics. 170cm height, 42 DOF, 20kg payload, 275 TOPS computing power. Advanced mobility and human-like interaction."
        keywords="PUDU D9, humanoid robot, bipedal robot, service robot, AI robot, dexterous hands, autonomous robot"
      />
      <Navigation />
      
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
        <div className="container mx-auto px-4 pt-12 pb-8 md:pt-16">
          {/* Back Button */}
          <Button asChild variant="ghost" className="mb-6">
            <Link to="/robots">
              <ArrowLeft className="mr-2 w-4 h-4" />
              Back to Robots
            </Link>
          </Button>

          {/* Hero Section */}
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            <div className="relative bg-white/5 rounded-lg p-8 flex items-center justify-center">
              <img 
                src={puduD9Img} 
                alt="PUDU D9 - Full-Sized Bipedal Humanoid Robot" 
                className="w-full h-auto max-h-[600px] object-contain"
              />
              <Badge className="absolute top-4 right-4 bg-primary text-primary-foreground font-bold text-lg px-4 py-2">
                PRE-ORDER OPEN
              </Badge>
            </div>

            <div className="flex flex-col justify-center space-y-6">
              <div>
                <p className="text-primary font-semibold mb-2">Full-Sized Bipedal Humanoid Robot</p>
                <h1 className="font-orbitron font-bold text-2xl md:text-4xl lg:text-5xl mb-4 break-words">PUDU D9</h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground mb-6 leading-relaxed">
                  The first full-sized bipedal humanoid robot by Pudu Robotics. Designed to match adult body proportions with high-precision perception and advanced mobility.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {specifications.slice(0, 4).map((spec, idx) => (
                  <Card key={idx} className="glass-effect border-[hsl(var(--line))]">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2 text-primary">
                        {spec.icon}
                        <p className="text-xs font-semibold">{spec.label}</p>
                      </div>
                      <p className="text-sm font-bold">{spec.value}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <div className="flex gap-4">
                <Button 
                  size="lg" 
                  className="flex-1 text-lg glow-gold"
                  onClick={() => setPreOrderOpen(true)}
                >
                  Pre-Order Now
                </Button>
                <Button 
                  asChild
                  size="lg" 
                  variant="outline" 
                  className="flex-1 text-lg"
                >
                  <a href="https://myascentium.com/ApplyNow/CommCredit?v=b106671d-0a80-40ca-aea6-5c888e426df6&s=dca75d8e-7184-4ed5-a7d0-f35ed9e35e86" target="_blank" rel="noopener noreferrer">
                    Apply for Financing
                  </a>
                </Button>
                <Button 
                  asChild 
                  size="lg" 
                  variant="outline" 
                  className="flex-1 text-lg"
                >
                  <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                    Book a Demo
                  </a>
                </Button>
              </div>
            </div>
          </div>

          {/* Detailed Information Tabs */}
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-3 mb-8">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="specifications">Specifications</TabsTrigger>
              <TabsTrigger value="applications">Applications</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-8">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Pioneering Humanoid Innovation</CardTitle>
                  <CardDescription>
                    The PUDU D9 represents a breakthrough in humanoid robotics, combining advanced AI, mechanical engineering, and human-centric design.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground leading-relaxed mb-6">
                    Standing at 170cm tall and weighing 65kg, the PUDU D9 is meticulously designed to match adult human proportions, enabling seamless integration into human environments. With 42 independent degrees of freedom and 352 N·m maximum joint torque, the D9 achieves fluid, natural movements that closely mimic human motion patterns.
                  </p>
                  <p className="text-muted-foreground leading-relaxed">
                    Equipped with PUDU DH11 dexterous hands capable of 20kg maximum payload, the D9 can perform complex manipulation tasks ranging from delicate object handling to heavy-duty operations. Its 275 TOPS computing power enables real-time perception, decision-making, and autonomous operation in dynamic environments.
                  </p>
                </CardContent>
              </Card>

              <div>
                <h2 className="font-orbitron text-2xl font-bold mb-6">Key Features</h2>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {keyFeatures.map((feature, idx) => (
                    <Card key={idx} className="glass-effect border-[hsl(var(--line))] hover:scale-105 transition-all duration-300">
                      <CardContent className="p-6">
                        <div className="text-primary mb-3">{feature.icon}</div>
                        <h3 className="font-bold text-lg mb-2">{feature.title}</h3>
                        <p className="text-sm text-muted-foreground">{feature.description}</p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="specifications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Technical Specifications</CardTitle>
                  <CardDescription>Detailed technical specifications of the PUDU D9 humanoid robot</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Physical Characteristics</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Height:</span> 170cm</p>
                          <p className="text-sm"><span className="font-semibold">Weight:</span> 65kg</p>
                          <p className="text-sm"><span className="font-semibold">Design:</span> Adult body proportions</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">DOF:</span> 42 independent degrees of freedom</p>
                          <p className="text-sm"><span className="font-semibold">Joint Torque:</span> 352 N·m maximum</p>
                          <p className="text-sm"><span className="font-semibold">Walking Speed:</span> Up to 2 m/s</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Manipulation & Payload</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Hands:</span> PUDU DH11 Dexterous Hands</p>
                          <p className="text-sm"><span className="font-semibold">Maximum Payload:</span> 20kg</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Manipulation:</span> Precision object handling</p>
                          <p className="text-sm"><span className="font-semibold">Grip Force:</span> Adaptive force control</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Computing & AI</h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">Computing Power:</span> 275 TOPS</p>
                          <p className="text-sm"><span className="font-semibold">Perception:</span> High-precision vision system</p>
                        </div>
                        <div className="space-y-2">
                          <p className="text-sm"><span className="font-semibold">AI Capabilities:</span> Real-time decision making</p>
                          <p className="text-sm"><span className="font-semibold">Navigation:</span> Autonomous path planning</p>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className="font-bold text-lg mb-4 text-primary">Safety & Compliance</h3>
                      <div className="space-y-2">
                        <p className="text-sm"><span className="font-semibold">Safety Systems:</span> Collision detection, force control, emergency stop</p>
                        <p className="text-sm"><span className="font-semibold">Human Interaction:</span> Safe compliant behavior in human environments</p>
                        <p className="text-sm"><span className="font-semibold">Certifications:</span> International safety standards compliance</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="applications" className="space-y-6">
              <Card className="glass-effect border-[hsl(var(--line))]">
                <CardHeader>
                  <CardTitle className="font-orbitron text-2xl">Industry Applications</CardTitle>
                  <CardDescription>
                    The PUDU D9 is designed for versatile deployment across multiple industries
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-6">
                    {applications.map((app, idx) => (
                      <div key={idx} className="space-y-2 p-4 rounded-lg bg-white/5 border border-[hsl(var(--line))]">
                        <h3 className="font-bold text-lg text-primary">{app.title}</h3>
                        <p className="text-sm text-muted-foreground">{app.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="glass-effect border-[hsl(var(--line))] bg-primary/5">
                <CardContent className="p-8 text-center">
                  <h3 className="font-orbitron text-2xl font-bold mb-4">Ready to Transform Your Operations?</h3>
                  <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
                    Be among the first to experience the future of humanoid robotics. Pre-order your PUDU D9 today and revolutionize how work gets done.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Button 
                      size="lg" 
                      className="glow-gold"
                      onClick={() => setPreOrderOpen(true)}
                    >
                      Pre-Order Now
                    </Button>
                    <Button asChild size="lg" variant="outline">
                      <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                        Schedule Consultation
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <Footer />
      <PreOrderForm 
        open={preOrderOpen} 
        onOpenChange={setPreOrderOpen}
        robotName="PUDU D9"
      />
    </>
  );
};

export default PuduD9;
